import React from 'react';
import { Heart, Users, Globe, Lightbulb } from 'lucide-react';

const FounderStorySection = () => {
  return (
    <div className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 mb-4 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium">
            💫 Our Story
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">
            The Journey to <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Conscious Business</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            From personal transformation to global movement - the story behind CNESS
          </p>
        </div>

        {/* Main Story - Centered Content */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="space-y-6 text-center">
            <h3 className="text-3xl font-bold text-gray-900">
              "From Personal Crisis to Purpose-Driven Mission"
            </h3>
            <p className="text-gray-700 leading-relaxed text-lg">
              CNESS was born from a profound personal journey. After experiencing the emptiness of traditional corporate success, our founder embarked on a transformative quest to discover what truly creates meaningful impact in business.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              Through years of deep exploration into consciousness, authentic leadership, and sustainable business practices, the vision for CNESS emerged: a world where businesses operate from consciousness, creating prosperity while honoring people, planet, and purpose.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              Today, CNESS represents the culmination of this journey - a comprehensive framework that helps organizations transform from the inside out, building businesses that are not just successful, but truly significant.
            </p>

            <div className="pt-6">
              <p className="text-lg text-gray-600 italic">
                "Business doesn't have to be a choice between profit and purpose. Consciousness triumphs when we integrate both."
              </p>
              <p className="text-lg font-bold text-gray-900 mt-3">
                - Nandhiji, Founder, CNESS
              </p>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="h-8 w-8 text-white" />
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Authenticity</h4>
            <p className="text-sm text-gray-600">Leading from genuine values and truth</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-white" />
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Community</h4>
            <p className="text-sm text-gray-600">Building networks of conscious leaders</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="h-8 w-8 text-white" />
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Global Impact</h4>
            <p className="text-sm text-gray-600">Creating change that matters worldwide</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lightbulb className="h-8 w-8 text-white" />
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Innovation</h4>
            <p className="text-sm text-gray-600">Pioneering new paths in business</p>
          </div>
        </div>

        {/* Team Vision */}
        <div className="mt-16 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-12 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
            We envision a world where every business is a force for good - where consciousness drives decision-making, authenticity replaces pretense, and success is measured not just in profits, but in positive impact on people, planet, and communities.
          </p>
          <a href="/support">
            <button className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-full hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl">
              Join Our Mission
            </button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default FounderStorySection;